import { createLogger } from '../lib/log'
import type {
  AuthenticationEventSource,
  AuthenticationServiceInterface,
  AuthenticationState,
  AuthenticationToken,
} from '../types'
import type HostMessagingService from './HostMessagingService'

/**
 * The number of milliseconds before token expiry when a token refresh should
 * be initiated.
 */
const TOKEN_REFRESH_TOLERANCE = 1000 * 60 * 5

const { log } = createLogger('AuthenticationService')

/**
 * Authentication service implementation that maintains an authenticated session
 * with the GoodLifePlus Operator API on behalf of the host application.
 *
 * The host application owns the user authentication lifecycle (login and
 * logout). This service reacts to authentication state changes, exchanges
 * host-provided customer tokens for Operator API session tokens, refreshes
 * those session tokens before they expire, and publishes authentication state
 * changes to consumers.
 *
 * ```text
 * ┌─────────────┐       ┌───────────────────────┐        ┌────────────────────┐
 * │ HostService │       │ AuthenticationService │        │ Authentication API │
 * └────┬────────┘       └───────────┬───────────┘        └─────────┬──────────┘
 * ----------------------------[ HOST LOGIN ]-----------------------------------
 *      │                            │ {state=unauthenticated}      │
 *      ├─ authchange event (true) ─►│                              │
 *      │                            │                              │
 *     ┌│◄────────── customer token ─┤                              │
 *     └├───────────────────────────►├─ exchange ──────────────────►│┐
 *      │                            │◄─────────────────────────────│┘
 *      │                            │ {state=authenticated}        │
 *      ~                            ~                              ~
 *               ...Later, when token is close to expiry...
 *      ~                            ~                              ~
 *      │                            │ {state=reauthenticating}     │
 *     ┌│◄── refresh customer token ─┤                              │
 *     └│───────────────────────────►│ refresh ────────────────────►│┐
 *      │                            │◄─────────────────────────────│┘
 *      │                            │ {state=authenticated}        │
 * ---------------------------[ HOST LOGOUT ]-----------------------------------
 *      ├─ authchange event (false) ►│                              │
 *      │                            │ {state=unauthenticated}      │
 * ```
 */
export default class AuthenticationService
  implements AuthenticationEventSource, AuthenticationServiceInterface
{
  constructor(
    private readonly authUrl: string,
    private readonly operatorKeyId: string,
    private readonly siteId: string,
    private readonly hostService: HostMessagingService
  ) {
    // Listen for authentication changes from from the host application. If the
    // host notifies us of a login, trigger a pre-emptive token fetch. If the
    // host logs out, clear the auth settings.
    hostService.addEventListener('authchange', (event) => {
      const isAuthed = (event as CustomEvent<boolean>).detail
      if (isAuthed) {
        void this.getToken()
      } else {
        // Do we need to tell the GLP Operator API that the session ended?
        clearTimeout(this.tokenRefreshTimer)
        this.customerToken = null
        this.sessionToken = null
        this.setState('unauthenticated')
      }
    })
  }

  private events = new EventTarget()

  private currentState: AuthenticationState = 'unauthenticated'

  private customerToken: string | null = null

  private sessionToken: AuthenticationToken | null = null

  private tokenRefreshTimer: any

  private tokenPromise: Promise<string | null> | null = null

  onStateChange(
    listener: (
      state: AuthenticationState,
      previous: AuthenticationState
    ) => void
  ) {
    const handler = (e: Event) => {
      const args = (
        e as CustomEvent<{
          newState: AuthenticationState
          previousState: AuthenticationState
        }>
      ).detail
      listener(args.newState, args.previousState)
    }
    this.events.addEventListener('statechange', handler)
    return () => this.events.removeEventListener('statechange', handler)
  }

  get state(): AuthenticationState {
    return this.currentState
  }

  /**
   * Determines if the user is authenticated or not. Note: If the user is mid
   * reauthentication then `true` is returned
   */
  get isAuthenticated(): boolean {
    return this.state === 'authenticated' || this.state === 'reauthenticating'
  }

  async getToken(): Promise<string | null> {
    //  log('getToken()')

    // Return existing session token if we already have one and it's not about
    // to expire.
    if (this.sessionToken && !this.isTokenExpiringSoon()) {
      return this.sessionToken.token
    }

    // From here on, everything is async. Since this method could be called
    // multiple times, we wrap the work in a promise and return that to any
    // callers. This avoids multiple requests for a session token and the race
    // condition hell that would follow!

    // If authentication is already in progress, have the caller wait for it to
    // resolve.
    if (this.tokenPromise) {
      // log('Authentication already in progress')
      return this.tokenPromise
    }

    // If we have a token but it is expired/expiring, refresh it.
    if (this.sessionToken) {
      log('Token expired or expiring soon, refreshing...')

      // TODO: remove `Promise.resolve()` wrapper here - NOTE: check the
      // synchronous case below
      this.tokenPromise = Promise.resolve()
        .then(() => this.refreshToken())
        .then(() => this.sessionToken?.token ?? null)
        .finally(() => {
          this.tokenPromise = null
        })

      return this.tokenPromise
    }

    // Otherwise perform initial authentication.

    // `authenticate()` synchronously executes until its first `await`. It calls
    // `setState()`, which dispatches a synchronous event that can re-enter
    // `getToken()`. Wrapping the call in `Promise.resolve().then(...)` delays
    // execution until after `this.tokenPromise` has been assigned, ensuring
    // re-entrant callers receive the existing in-flight promise.
    this.tokenPromise = Promise.resolve()
      .then(() => this.authenticate())
      .finally(() => {
        this.tokenPromise = null
      })

    return this.tokenPromise
  }

  /**
   * Determines if a token is close to its refresh window, or not.
   *
   * @returns `true` if the token is within the refresh window, otherwise
   * `false`
   */
  private isTokenExpiringSoon(): boolean {
    if (!this.sessionToken) {
      return true
    }
    const expiresAt = this.sessionToken.expires_at * 1000
    return expiresAt - Date.now() <= TOKEN_REFRESH_TOLERANCE
  }

  /**
   * Authenticates the application session using credentials provided by the
   * host.
   *
   * This method does not initiate a user login flow. The host application is
   * responsible for authenticating the user and providing a customer token.
   * This method exchanges that token for a session token used by this service
   * and updates the authentication state accordingly.
   *
   * If authentication succeeds:
   * - The session token is stored.
   * - Token refresh scheduling is configured.
   * - The authentication state transitions to `authenticated`.
   *
   * If authentication fails:
   * - Any cached authentication tokens are cleared.
   * - The authentication state transitions to `unauthenticated`.
   * - `null` is returned.
   *
   * @returns The session token string when authentication succeeds, otherwise
   * `null`.
   */
  private async authenticate() {
    this.setState('authenticating')
    try {
      if (!this.customerToken) {
        log('No customer token - requesting one from the host...')
        const { token } = await this.hostService.requestCustomerToken()
        log('Got customer token', token)
        this.customerToken = token
      } else {
        log('Already have a customer token..')
      }

      log('Exchanging customer token for a session token')
      const sessionToken = await this.exchange(this.customerToken)

      log('Got Session Token')
      this.updateToken(sessionToken)
      this.setState('authenticated')

      return sessionToken.token
    } catch (e) {
      console.error(e)
      this.customerToken = null
      this.sessionToken = null
      this.setState('unauthenticated')
      return null
    } finally {
      log('Authentication complete')
    }
  }

  /**
   * Updates the current authentication state and notifies listeners of changes.
   *
   * If the requested state is the same as the current state, no update or event
   * dispatch occurs.
   *
   * @param newState The new authentication state to apply.
   */
  private setState(newState: AuthenticationState) {
    if (this.currentState === newState) {
      return
    }
    log(`setState "${newState}"`)
    const previousState = this.currentState
    this.currentState = newState
    this.events.dispatchEvent(
      new CustomEvent('statechange', { detail: { newState, previousState } })
    )
  }

  /**
   * Exchanges a customerToken (boot JWT) for a Sanctum bearer token. Called
   * when there is no initial state.
   *
   * @param customerToken
   * @returns The exchanged token
   */
  private async exchange(customerToken: string): Promise<AuthenticationToken> {
    const response = await fetch(`${this.authUrl}/player/auth/v1/session/exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Integrator-Key-Id': this.operatorKeyId,
        'X-Site-Id': this.siteId,
      },
      body: JSON.stringify({ token: customerToken }),
    })

    if (response.ok) {
      const res = await response.json()
      return res.data as Promise<AuthenticationToken>
    } else {
      throw new Error('Authentication exchange failed')
    }
  }

  /**
   * Refreshes the current application session token.
   *
   * Uses the existing session token to request a refreshed customer token from
   * the host, then exchanges it for a new session token. If successful, the
   * authentication state is restored to `authenticated` and the token expiry
   * timer is updated.
   *
   * If the refresh fails:
   * - The current session token is cleared.
   * - The authentication state transitions to `unauthenticated`.
   * - `null` is returned.
   *
   * This method does not initiate a user login flow. The host application is
   * responsible for managing user authentication.
   *
   * @returns Resolves when the token has been refreshed, or `null` if the
   * refresh fails or there is no active session.
   */
  public async refreshToken() {
    if (!this.sessionToken) {
      return
    }

    this.setState('reauthenticating')

    try {
      const customerRefreshResponse =
        await this.hostService.requestCustomerTokenRefresh(
          this.sessionToken.token
        )
      const response = await fetch(`${this.authUrl}/player/auth/v1/session/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.sessionToken?.token}`,
          'Content-Type': 'application/json',
          'X-Integrator-Key-Id': this.operatorKeyId,
          'X-Site-Id': this.siteId,
        },
        body: JSON.stringify({ token: customerRefreshResponse.token }),
      })

      if (response.ok) {
        const res = await response.json()
        this.updateToken(res.data as AuthenticationToken)
        this.setState('authenticated')
      } else {
        throw new Error('Authentication refresh failed')
      }
    } catch (e) {
      clearTimeout(this.tokenRefreshTimer)
      this.sessionToken = null
      this.setState('unauthenticated')
      console.error(e)
      return null
    }
  }

  /**
   * Replaces an existing token with a new one.
   *
   * Also schedules a timer to automatically handle the next refresh. Note:
   * timers are notoriously unpredicatable over long durations (browser
   * throttling, suspended tabs, sleep/wake, etc.). Therefore expiry is also
   * checked calling `getToken()`
   *
   * @param token The new token to use
   */
  private updateToken(token: AuthenticationToken) {
    this.sessionToken = token
    const expiresIn =
      token.expires_at * 1000 - Date.now() - TOKEN_REFRESH_TOLERANCE
    log(`Updated token. Refresh scheduled for ${expiresIn.toFixed()} seconds`)

    // Stop any active timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer)
    }

    // Optional optimisation: proactively refresh before expiry.
    this.tokenRefreshTimer = setTimeout(() => this.getToken(), expiresIn)
  }
}
